/*! boroj-chat.js — Boroj AI Chat widget controller.
 * Spec: docs/superpowers/specs/2026-06-28-boroj-chat-widget-design.md
 * Owns the custom launcher + panel: open/close, animations origin, session open-state, live RTL/LTR
 * re-sync, and a thin ENGINE ADAPTER that lazy-loads @n8n/chat on FIRST open and themes it.
 * Isolated: single guarded global, no reads/writes of BorojBrowse or any other module.
 *
 * Extensibility (no shell change needed): everything below the panel header is the adapter's concern.
 *   - quick replies / suggested questions -> render chips in `data-bchat-extras`, call api.send(text)
 *   - file uploads -> pass n8n option in createEngine()
 *   - product cards / human handoff -> adapter renders into the message stream / a header action
 *   - swap engine entirely -> replace createEngine(); the launcher + panel never change.
 */
window.BorojChat = window.BorojChat || (function () {
  'use strict';

  function init() {
    var root = document.getElementById('boroj-chat');
    if (!root || root.getAttribute('data-bchat-ready') === '1') return;
    root.setAttribute('data-bchat-ready', '1');

    var launcher = document.getElementById('boroj-chat-launcher');
    var panel = document.getElementById('boroj-chat-panel');
    var mount = root.querySelector('[data-bchat-mount]');
    var loading = root.querySelector('.bchat-body__loading');
    var closeBtn = root.querySelector('[data-bchat-close]');

    var cfg = {
      webhook: root.getAttribute('data-webhook') || '',
      welcome: root.getAttribute('data-welcome') || '',
      placeholder: root.getAttribute('data-placeholder') || '',
      errorText: root.getAttribute('data-error') || '',
      streaming: root.getAttribute('data-streaming') === 'true',
      n8nCss: root.getAttribute('data-n8n-css') || '',
      n8nJs: root.getAttribute('data-n8n-js') || ''
    };
    var state = { open: false, engine: 'idle' /* idle | loading | ready | error */ };

    /* ---- direction-aware transform origin (the panel grows from the launcher corner) ---- */
    function syncDir() {
      var rtl = false;
      try { rtl = getComputedStyle(document.documentElement).direction === 'rtl'; } catch (e) {}
      root.style.setProperty('--bchat-origin-x', rtl ? 'left' : 'right');
    }

    /* ---- open / close (+ session-scoped persistence of the open state) ---- */
    function open() {
      if (state.open) return;
      state.open = true;
      root.classList.add('is-open');
      launcher.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      try { sessionStorage.setItem('boroj-chat-open', '1'); } catch (e) {}
      ensureEngine();
    }
    function close() {
      if (!state.open) return;
      state.open = false;
      root.classList.remove('is-open');
      launcher.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      try { sessionStorage.setItem('boroj-chat-open', '0'); } catch (e) {}
    }
    function toggle() { state.open ? close() : open(); }

    /* ---- ENGINE ADAPTER: lazy-load @n8n/chat on first open, themed, mounted in the panel body ---- */
    function ensureEngine() {
      if (state.engine === 'ready' || state.engine === 'loading') return;
      if (!cfg.webhook || !cfg.n8nJs) { showError(); return; }
      state.engine = 'loading';
      if (cfg.n8nCss && !document.querySelector('link[data-bchat-n8n-css]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = cfg.n8nCss; link.setAttribute('data-bchat-n8n-css', '');
        document.head.appendChild(link);
      }
      createEngine().then(function () {
        state.engine = 'ready';
        if (loading) loading.remove();
      }).catch(function (err) {
        state.engine = 'error'; showError();
        if (window.console) console.warn('[boroj-chat] engine failed to load', err);
      });
    }

    // The ONLY n8n-specific code. Swap this to change/replace the conversation engine.
    function createEngine() {
      return loadCreateChat().then(function (createChat) {
        createChat({
          webhookUrl: cfg.webhook,
          target: mount,
          mode: 'fullscreen',
          showWelcomeScreen: false,
          loadPreviousSession: true,
          enableStreaming: cfg.streaming, // off by default; n8n agent node must stream end-to-end to enable
          initialMessages: cfg.welcome ? [cfg.welcome] : [],
          defaultLanguage: 'en',
          i18n: { en: {
            title: '', subtitle: '', footer: '', getStarted: '',
            inputPlaceholder: cfg.placeholder, closeButtonTooltip: ''
          } }
        });
      });
    }

    // GOTCHA: Shopify's asset minifier rewrites a literal `import()` in a .js asset into `require()`
    // (undefined in-browser). So we keep the dynamic import inside a runtime <script type="module">
    // whose body is a STRING — the minifier can't touch string contents, and the browser runs it as a
    // real ES module where import() works. Resolves with the n8n `createChat` export.
    function loadCreateChat() {
      return new Promise(function (resolve, reject) {
        if (window.__borojCreateChat) { resolve(window.__borojCreateChat); return; }
        window.__borojN8nReady = function (createChat, err) {
          if (createChat) { window.__borojCreateChat = createChat; resolve(createChat); }
          else { reject(err || new Error('n8n load failed')); }
        };
        var s = document.createElement('script');
        s.type = 'module';
        s.textContent = 'import(' + JSON.stringify(cfg.n8nJs) +
          ').then(function(m){window.__borojN8nReady(m.createChat);}).catch(function(e){window.__borojN8nReady(null,e);});';
        s.onerror = function () { reject(new Error('n8n script error')); };
        document.head.appendChild(s);
      });
    }

    function showError() {
      if (!loading) return;
      loading.classList.add('bchat-body__loading--error');
      loading.textContent = cfg.errorText || 'تعذّر تحميل المحادثة';
    }

    /* ---- wiring ---- */
    launcher.addEventListener('click', toggle);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && state.open) close(); });

    syncDir();
    // Switcher is reload-based, so persistence handles language change; this is belt-and-suspenders for
    // any live <html dir/lang> change — re-sync direction WITHOUT tearing down the engine (session kept).
    try {
      new MutationObserver(syncDir).observe(document.documentElement, { attributes: true, attributeFilter: ['dir', 'lang'] });
    } catch (e) {}

    // restore open state for this browsing session (reopens after navigation/language switch)
    try { if (sessionStorage.getItem('boroj-chat-open') === '1') open(); } catch (e) {}

    return { open: open, close: close, toggle: toggle, _state: state };
  }

  var api = null;
  function boot() { api = init() || api; }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  return {
    open: function () { api && api.open(); },
    close: function () { api && api.close(); },
    toggle: function () { api && api.toggle(); }
  };
})();
